import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
	Button,
	TableBody,
	TableRow,
	TableCell,
	IconButton,
	Table,
	TableHead,
	Paper,
	Tooltip,
	Typography,
	CircularProgress,
	Chip,
} from "@material-ui/core";
import {
	Edit,
	CheckCircle,
	DeleteOutline,
	FileCopy,
	SignalCellularConnectedNoInternet2Bar,
	SignalCellularConnectedNoInternet0Bar,
	SignalCellular4Bar,
	CropFree,
	PhoneAndroid,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import BridgeDevicePairing from "../../components/BridgeDevicePairing";
import BridgeKeepAwakeConfig from "../../components/BridgeKeepAwakeConfig";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	customTableCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	tooltip: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		fontSize: theme.typography.pxToRem(14),
		border: "1px solid #dadde9",
		maxWidth: 450,
	},
	tooltipPopper: {
		textAlign: "center",
	},
	buttonProgress: {
		color: green[500],
	},
	bridgePanel: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		backgroundColor: theme.palette.background.default,
	},
	bridgeRow: {
		display: "flex",
		gap: theme.spacing(1),
		flexWrap: "wrap",
		alignItems: "center",
		marginTop: theme.spacing(1),
	},
}));

const CustomToolTip = ({ title, content, children }) => {
	const classes = useStyles();

	return (
		<Tooltip
			arrow
			classes={{
				tooltip: classes.tooltip,
				popper: classes.tooltipPopper,
			}}
			title={
				<React.Fragment>
					<Typography gutterBottom color="inherit">
						{title}
					</Typography>
					{content && <Typography>{content}</Typography>}
				</React.Fragment>
			}
		>
			{children}
		</Tooltip>
	);
};

const parseBridgeSession = raw => {
	if (!raw) return {};
	try {
		return JSON.parse(raw);
	} catch (err) {
		return {};
	}
};

const resolveConnectionMode = (session, bridgeEnabled, usbOnlyMode) => {
	const data = parseBridgeSession(session);
	if (
		data.connectionMode === "web" ||
		data.connectionMode === "hybrid" ||
		data.connectionMode === "usb"
	) {
		return data.connectionMode;
	}
	if (usbOnlyMode) {
		return "usb";
	}
	return bridgeEnabled ? "hybrid" : "web";
};

const connectionModeLabel = mode => {
	if (mode === "usb") {
		return i18n.t("connections.connectionMode.usb");
	}
	if (mode === "hybrid") {
		return i18n.t("connections.connectionMode.hybrid");
	}
	return i18n.t("connections.connectionMode.web");
};

const Connections = () => {
	const classes = useStyles();

	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);
	const [bridgeStatus, setBridgeStatus] = useState(null);
	const [bridgeEnabled, setBridgeEnabled] = useState(false);
	const usbOnlyMode = bridgeStatus?.usbOnly === true;

	useEffect(() => {
		let active = true;

		const loadBridgeStatus = async () => {
			try {
				const { data } = await api.get("/android-bridge/status");
				if (active) {
					setBridgeStatus(data);
					setBridgeEnabled(true);
				}
			} catch (err) {
				if (active) {
					setBridgeEnabled(false);
					setBridgeStatus(null);
				}
			}
		};

		loadBridgeStatus();
		const timer = setInterval(loadBridgeStatus, 10000);
		return () => {
			active = false;
			clearInterval(timer);
		};
	}, []);


	const handleCopyWhatsappId = async whatsAppId => {
		try {
			await navigator.clipboard.writeText(String(whatsAppId));
			toast.success(i18n.t("connections.toasts.whatsappIdCopied"));
		} catch (err) {
			toast.info(String(whatsAppId));
		}
	};

	const renderBridgePanel = () => {
		if (!bridgeEnabled) {
			return null;
		}

		const devices = bridgeStatus?.devices || [];

		return (
			<Paper className={classes.bridgePanel} variant="outlined">
				<Typography variant="subtitle1">
					{usbOnlyMode
						? i18n.t("connections.bridge.usbTitle")
						: i18n.t("connections.bridge.hybridTitle")}
				</Typography>
				<div className={classes.bridgeRow}>
					<Chip
						size="small"
						color={bridgeStatus?.agentOnline ? "primary" : "default"}
						label={
							bridgeStatus?.agentOnline
								? i18n.t("connections.bridge.agentOnline")
								: i18n.t("connections.bridge.agentOffline")
						}
					/>
					{devices.map(device => (
						<Chip
							key={device.serial}
							size="small"
							variant="outlined"
							icon={<PhoneAndroid />}
							label={`${device.label || device.serial}`}
						/>
					))}
				</div>
				<Typography variant="body2" style={{ marginTop: 8 }}>
					{usbOnlyMode
						? i18n.t("connections.bridge.usbInstructions")
						: i18n.t("connections.bridge.hybridInstructions")}
				</Typography>
				<Typography variant="caption" display="block">
					{i18n.t("connections.bridge.escrcpyHint")}
				</Typography>
			</Paper>
		);
	};

	const handleStartWhatsAppSession = async whatsAppId => {
		try {
			await api.post(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleRequestNewQrCode = async whatsAppId => {
		try {
			await api.put(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId: whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
	};

	const renderActionButtons = whatsApp => {
		const bridgeData = parseBridgeSession(whatsApp.session);
		const connectionMode = resolveConnectionMode(
			whatsApp.session,
			bridgeEnabled,
			usbOnlyMode
		);
		const isHybridMode = connectionMode === "hybrid";
		const isUsbMode = connectionMode === "usb" || usbOnlyMode;
		const isWebMode = connectionMode === "web";
		const canPairUsb =
			bridgeEnabled &&
			(isUsbMode || isHybridMode) &&
			!bridgeData.deviceSerial &&
			(isUsbMode
				? [
						"OPENING",
						"WAITING_AGENT",
						"DISCONNECTED",
						"DEVICE_OFFLINE",
				  ].includes(whatsApp.status)
				: ["qrcode", "WAITING_AGENT", "CONNECTED"].includes(whatsApp.status));

		return (
			<>
				{canPairUsb && (
					<BridgeDevicePairing
						whatsAppId={whatsApp.id}
						compact
						initialDeviceSerial={bridgeData.deviceSerial}
					/>
				)}
				{bridgeData.deviceSerial && bridgeEnabled && (isHybridMode || isUsbMode) && (
					<BridgeKeepAwakeConfig
						whatsAppId={whatsApp.id}
						initialMinutes={bridgeData.keepAwakeMinutes || 0}
						disabled={!bridgeStatus?.agentOnline}
					/>
				)}
				{!isUsbMode && whatsApp.status === "qrcode" && (
					<Button
						size="small"
						variant="contained"
						color="primary"
						onClick={() => handleOpenQrModal(whatsApp)}
					>
						{i18n.t("connections.buttons.qrcode")}
					</Button>
				)}
				{whatsApp.status === "OPENING" && isWebMode && !isUsbMode && (
					<>
						<Button
							size="small"
							variant="contained"
							color="primary"
							onClick={() => handleOpenQrModal(whatsApp)}
						>
							{i18n.t("connections.buttons.qrcode")}
						</Button>{" "}
						<Button
							size="small"
							variant="outlined"
							color="primary"
							onClick={() => handleStartWhatsAppSession(whatsApp.id)}
						>
							{i18n.t("connections.buttons.tryAgain")}
						</Button>
					</>
				)}
				{whatsApp.status === "OPENING" && (isUsbMode || !isWebMode) && (
					<Button size="small" variant="outlined" disabled color="default">
						{i18n.t("connections.buttons.connecting")}
					</Button>
				)}
				{whatsApp.status === "DISCONNECTED" && (
					<>
						<Button
							size="small"
							variant="outlined"
							color="primary"
							onClick={() => handleStartWhatsAppSession(whatsApp.id)}
						>
							{i18n.t("connections.buttons.tryAgain")}
						</Button>
						{!isUsbMode && (
							<>
								{" "}
								<Button
									size="small"
									variant="outlined"
									color="secondary"
									onClick={() => handleRequestNewQrCode(whatsApp.id)}
								>
									{i18n.t("connections.buttons.newQr")}
								</Button>
							</>
						)}
					</>
				)}
				{(whatsApp.status === "CONNECTED" ||
					whatsApp.status === "DEVICE_CONNECTED" ||
					whatsApp.status === "PAIRING" ||
					whatsApp.status === "TIMEOUT") && (
					<Button
						size="small"
						variant="outlined"
						color="secondary"
						onClick={() => {
							handleOpenConfirmationModal("disconnect", whatsApp.id);
						}}
					>
						{i18n.t("connections.buttons.disconnect")}
					</Button>
				)}
				{whatsApp.status === "DEVICE_OFFLINE" && (
					<Button
						size="small"
						variant="outlined"
						color="primary"
						onClick={() => handleStartWhatsAppSession(whatsApp.id)}
					>
						{i18n.t("connections.buttons.tryAgain")}
					</Button>
				)}
			</>
		);
	};

	const renderStatusToolTips = whatsApp => {
		const bridgeData = parseBridgeSession(whatsApp.session);
		const connectionMode = resolveConnectionMode(
			whatsApp.session,
			bridgeEnabled,
			usbOnlyMode
		);
		const isHybridMode = connectionMode === "hybrid";
		const isUsbMode = connectionMode === "usb" || usbOnlyMode;

		return (
			<div className={classes.customTableCell}>
				{whatsApp.status === "DISCONNECTED" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.disconnected.title")}
						content={i18n.t("connections.toolTips.disconnected.content")}
					>
						<SignalCellularConnectedNoInternet0Bar color="secondary" />
					</CustomToolTip>
				)}
				{whatsApp.status === "OPENING" && (
					<CircularProgress size={24} className={classes.buttonProgress} />
				)}
				{!isUsbMode && whatsApp.status === "qrcode" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.qrcode.title")}
						content={i18n.t("connections.toolTips.qrcode.content")}
					>
						<CropFree />
					</CustomToolTip>
				)}
				{whatsApp.status === "CONNECTED" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.connected.title")}
						content={
							isUsbMode && bridgeData.deviceSerial
								? `${i18n.t(
										"connections.toolTips.deviceConnected.content"
								  )} ${bridgeData.deviceLabel || bridgeData.deviceSerial}`
								: isHybridMode && bridgeData.deviceSerial
								? `${i18n.t(
										"connections.toolTips.hybridConnected.content"
								  )} USB: ${bridgeData.deviceLabel || bridgeData.deviceSerial}`
								: isHybridMode
								? i18n.t("connections.toolTips.hybridConnected.content")
								: i18n.t("connections.toolTips.webOnlyConnected.content")
						}
					>
						<SignalCellular4Bar style={{ color: green[500] }} />
					</CustomToolTip>
				)}
				{whatsApp.status === "DEVICE_CONNECTED" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.deviceConnected.title")}
						content={
							bridgeData.deviceLabel
								? `${bridgeData.deviceLabel} (${bridgeData.deviceSerial || ""})`
								: i18n.t("connections.toolTips.deviceConnected.content")
						}
					>
						<PhoneAndroid style={{ color: green[500] }} />
					</CustomToolTip>
				)}
				{whatsApp.status === "WAITING_AGENT" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.waitingAgent.title")}
						content={i18n.t("connections.toolTips.waitingAgent.content")}
					>
						<CircularProgress size={24} className={classes.buttonProgress} />
					</CustomToolTip>
				)}
				{whatsApp.status === "DEVICE_OFFLINE" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.deviceOffline.title")}
						content={i18n.t("connections.toolTips.deviceOffline.content")}
					>
						<SignalCellularConnectedNoInternet2Bar color="secondary" />
					</CustomToolTip>
				)}
				{(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.timeout.title")}
						content={i18n.t("connections.toolTips.timeout.content")}
					>
						<SignalCellularConnectedNoInternet2Bar color="secondary" />
					</CustomToolTip>
				)}
			</div>
		);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
				bridgeEnabled={bridgeEnabled}
				usbOnlyMode={usbOnlyMode}
			/>
			<MainHeader>
				<Title>{i18n.t("connections.title")}</Title>
				<MainHeaderButtonsWrapper>
					<Button
						variant="contained"
						color="primary"
						onClick={handleOpenWhatsAppModal}
					>
						{i18n.t("connections.buttons.add")}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<Paper className={classes.mainPaper} variant="outlined">
				{renderBridgePanel()}
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="center">
								{i18n.t("connections.table.name")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.whatsappId")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.status")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.connectionMode")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.session")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.lastUpdate")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.default")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.counterPhone")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.autoDisableCounter")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.actions")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRowSkeleton />
						) : (
							<>
								{whatsApps?.length > 0 &&
									whatsApps.map(whatsApp => (
										<TableRow key={whatsApp.id}>
											<TableCell align="center">{whatsApp.name}</TableCell>
											<TableCell align="center">
												<div className={classes.customTableCell}>
													<Tooltip
														title={i18n.t(
															"connections.table.whatsappIdHint"
														)}
													>
														<Chip
															size="small"
															color="primary"
															label={whatsApp.id}
															onClick={() =>
																handleCopyWhatsappId(whatsApp.id)
															}
															style={{ cursor: "pointer" }}
														/>
													</Tooltip>
													<Tooltip
														title={i18n.t(
															"connections.table.copyWhatsappId"
														)}
													>
														<IconButton
															size="small"
															onClick={() =>
																handleCopyWhatsappId(whatsApp.id)
															}
														>
															<FileCopy fontSize="small" />
														</IconButton>
													</Tooltip>
												</div>
											</TableCell>
											<TableCell align="center">
												{renderStatusToolTips(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{bridgeEnabled ? (
													<Chip
														size="small"
														color={
															resolveConnectionMode(
																whatsApp.session,
																bridgeEnabled,
																usbOnlyMode
															) === "web"
																? "default"
																: "primary"
														}
														variant="outlined"
														label={connectionModeLabel(
															resolveConnectionMode(
																whatsApp.session,
																bridgeEnabled,
																usbOnlyMode
															)
														)}
													/>
												) : (
													"—"
												)}
											</TableCell>
											<TableCell align="center">
												{renderActionButtons(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
											</TableCell>
											<TableCell align="center">
												{whatsApp.isDefault && (
													<div className={classes.customTableCell}>
														<CheckCircle style={{ color: green[500] }} />
													</div>
												)}
											</TableCell>
											<TableCell align="center">
												{whatsApp.counterPhone || "—"}
											</TableCell>
											<TableCell align="center">
												{whatsApp.autoDisableCounter ? (
													<Chip
														size="small"
														color="primary"
														label={i18n.t("connections.autoDisable.active")}
													/>
												) : (
													"—"
												)}
											</TableCell>
											<TableCell align="center">
												<IconButton
													size="small"
													onClick={() => handleEditWhatsApp(whatsApp)}
												>
													<Edit />
												</IconButton>

												<IconButton
													size="small"
													onClick={e => {
														handleOpenConfirmationModal("delete", whatsApp.id);
													}}
												>
													<DeleteOutline />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
							</>
						)}
					</TableBody>
				</Table>
			</Paper>
		</MainContainer>
	);
};

export default Connections;
